import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  IconButton,
  SvgIcon,
  makeStyles
} from '@material-ui/core';
import { Menu as MenuIcon } from 'react-feather';
import { useSelector } from 'react-redux';
import SiteFooter from 'src/components/SiteFooter';
import useSettings from 'src/hooks/useSettings';
import { THEME_MODES } from 'src/utils/resolveTheme';
import { INVENTORY_UI_FLAGS } from 'src/views/listView/inventoryUiFlags';
import {
  INVENTORY_APP_MENU_RIGHT_PX,
  INVENTORY_APP_MENU_SIZE_PX,
  INVENTORY_LIGHT,
  INVENTORY_LOGGED_IN_MOBILE_CHROME_HEIGHT_PX,
  LOGGED_IN_MOBILE_CHROME_ICON_COLOR,
  LOGGED_IN_MOBILE_CHROME_SIDE_INSET_PX,
} from 'src/views/listView/inventoryTheme';

import HomeNavBar from 'src/layouts/HomeLayout/NavBar';
import TopBar, {
  HOME_TOP_BAR_HEIGHT_MOBILE_PX,
  HOME_TOP_BAR_HEIGHT_PX,
  homeTopBarHeightCss,
} from 'src/layouts/HomeLayout/TopBar';
import DashboardNavBar from 'src/layouts/DashboardLayout/NavBar';
import InventoryChromeContext from './InventoryChromeContext';

const HORIZONTAL_LOGO_SRC = '/static/news/1000containers-horizontal-white.svg';

/**
 * Stable layout for `/app/solar-panels`.
 *
 * Always shows the product-page TopBar. Logged-in users also get the share /
 * logo / app-menu chrome below it (same stacking as News). Layout identity stays
 * fixed across guest ↔ logged-in so unlock-modal login does not remount inventory.
 *
 * Light desktop: app menu is rendered in-flow next to the search card (listView)
 * so its gap matches filter→slider spacing; the fixed button is mobile/tablet only.
 */
const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    width: '100%',
  },
  rootInventoryLightMobile: {
    [theme.breakpoints.down('md')]: {
      backgroundColor: INVENTORY_LIGHT.pageBg,
    },
  },
  topBarLoggedInMobileHidden: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  wrapper: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
    paddingTop: homeTopBarHeightCss(HOME_TOP_BAR_HEIGHT_MOBILE_PX),
    [theme.breakpoints.up('sm')]: {
      paddingTop: homeTopBarHeightCss(HOME_TOP_BAR_HEIGHT_PX),
    },
  },
  /** Logged-in mobile: share/logo/menu action bar (TopBar is removed). */
  wrapperLoggedInMobileHeader: {
    [theme.breakpoints.down('sm')]: {
      paddingTop: INVENTORY_LOGGED_IN_MOBILE_CHROME_HEIGHT_PX,
    },
  },
  contentContainer: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
  },
  content: {
    flex: '1 1 auto',
    height: '100%',
    overflow: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  main: {
    flex: '1 1 auto',
  },
  /**
   * Logged-in app menu — under TopBar (mobile / stacked inventory only).
   * Light desktop uses the in-flow control beside search instead.
   */
  MenuIconButton: {
    position: 'fixed',
    color: 'white',
    top: HOME_TOP_BAR_HEIGHT_PX + 27,
    right: INVENTORY_APP_MENU_RIGHT_PX,
    [theme.breakpoints.down('sm')]: {
      /** Match guest TopBar menu: top of bar, right: 24, no circle. */
      top: 0,
      right: LOGGED_IN_MOBILE_CHROME_SIDE_INSET_PX,
      width: INVENTORY_LOGGED_IN_MOBILE_CHROME_HEIGHT_PX,
      height: INVENTORY_LOGGED_IN_MOBILE_CHROME_HEIGHT_PX,
      backgroundColor: 'transparent',
      border: 'none',
      boxShadow: 'none',
      borderRadius: 0,
      color: LOGGED_IN_MOBILE_CHROME_ICON_COLOR,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
    padding: 0,
    backgroundColor: '#191f29',
    border: 'solid 1px #0c111a',
    width: INVENTORY_APP_MENU_SIZE_PX,
    height: INVENTORY_APP_MENU_SIZE_PX,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.drawer - 50,
  },
  /** Hide fixed menu where listView renders the in-flow desktop control. */
  MenuIconButtonHideOnLightDesktop: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  /** Light inventory: white circle chrome (matches ThemeModeToggle). Desktop / tablet only. */
  MenuIconButtonLight: {
    [theme.breakpoints.up('md')]: {
      backgroundColor: '#ffffff',
      border: 'solid 1px rgba(15, 23, 42, 0.12)',
      color: '#0F172A',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      '&:hover': {
        backgroundColor: '#F8FAFC',
      },
    },
  },
  share: {
    color: 'white',
    fontSize: '20px',
    width: '26px',
    height: '26px',
  },
  shareLight: {
    [theme.breakpoints.up('md')]: {
      color: '#0F172A',
    },
  },
  /** Mobile: match guest TopBar MenuIcon size (24px). */
  shareMobileChrome: {
    [theme.breakpoints.down('sm')]: {
      '&&': {
        color: LOGGED_IN_MOBILE_CHROME_ICON_COLOR,
        fontSize: '24px',
        width: 24,
        height: 24,
      },
    },
  },
}));

function InventoryRouteLayout({ children }) {
  const classes = useStyles();
  const { settings } = useSettings();
  const [isHomeNavOpen, setHomeNavOpen] = useState(false);
  const [isDashNavOpen, setDashNavOpen] = useState(false);
  const user = useSelector((state) => state.account.user);
  const inventoryLightPreferred = !INVENTORY_UI_FLAGS.enableDarkTheme
    || settings.themeMode === THEME_MODES.LIGHT;

  // Close drawers when auth chrome swaps.
  useEffect(() => {
    setHomeNavOpen(false);
    setDashNavOpen(false);
  }, [user]);

  const chromeValue = useMemo(() => ({
    openAppMenu: () => setDashNavOpen(true),
  }), []);

  return (
    <InventoryChromeContext.Provider value={chromeValue}>
      <div
        className={clsx(
          classes.root,
          user && inventoryLightPreferred && classes.rootInventoryLightMobile
        )}
      >
        <TopBar
          className={user ? classes.topBarLoggedInMobileHidden : undefined}
          onMobileNavOpen={() => (
            user ? setDashNavOpen(true) : setHomeNavOpen(true)
          )}
          hideLoginButton={Boolean(user)}
          centerNavLinks
          logoSrc={HORIZONTAL_LOGO_SRC}
        />
        {!user ? (
          <HomeNavBar
            onMobileClose={() => setHomeNavOpen(false)}
            openMobile={isHomeNavOpen}
            hideLoginButton={false}
          />
        ) : null}

        {user ? (
          <>
            <IconButton
              className={clsx(
                classes.MenuIconButton,
                inventoryLightPreferred && classes.MenuIconButtonLight,
                inventoryLightPreferred && classes.MenuIconButtonHideOnLightDesktop,
              )}
              onClick={() => setDashNavOpen(true)}
              aria-label="Open menu"
            >
              <SvgIcon
                className={clsx(
                  classes.share,
                  inventoryLightPreferred && classes.shareLight,
                  classes.shareMobileChrome,
                )}
              >
                <MenuIcon />
              </SvgIcon>
            </IconButton>
            <DashboardNavBar
              includePublicNav
              onMobileClose={() => setDashNavOpen(false)}
              openMobile={isDashNavOpen}
            />
          </>
        ) : null}

        <div
          className={clsx(
            classes.wrapper,
            user && classes.wrapperLoggedInMobileHeader
          )}
        >
          <div className={classes.contentContainer}>
            <div className={classes.content}>
              <div className={classes.main}>
                {children}
              </div>
              <SiteFooter />
            </div>
          </div>
        </div>
      </div>
    </InventoryChromeContext.Provider>
  );
}

InventoryRouteLayout.propTypes = {
  children: PropTypes.any
};

export default InventoryRouteLayout;
